import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
// INTERFACES
import type {
  OpcUaDataValueForm,
  OpcUaMethodMappingForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;

const BLANK_ARGUMENT: OpcUaDataValueForm = { kind: "string", value: "" };

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop). */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

/** Ordered input-arguments list for one method call — add/remove/edit each typed value. */
function InputArgumentsEditor({
  args,
  onChangeArgs,
}: {
  args: OpcUaDataValueForm[];
  onChangeArgs: (args: OpcUaDataValueForm[]) => void;
}) {
  const [draft, setDraft] = useState<OpcUaDataValueForm>({ ...BLANK_ARGUMENT });
  const onAdd = () => {
    onChangeArgs([...args, draft]);
    setDraft({ ...BLANK_ARGUMENT });
  };
  const onRemove = (index: number) => onChangeArgs(args.filter((_, i) => i !== index));

  return (
    <div className="mt-2">
      <span className="generalStyles__inputLabel">Input Arguments ({args.length})</span>
      {args.map((arg, index) => (
        <div
          key={`${index}-${arg.kind}-${arg.value}`}
          className="mb-2 generalStyles__flex"
          style={{ backgroundColor: "#161616", borderRadius: "5px", padding: "8px" }}
        >
          <div style={{ width: "100%" }}>
            <div className="generalStyles__textEllipsis">
              [{index}] {arg.kind} = {arg.value}
            </div>
          </div>
          <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
            <Tooltip title="Remove argument">
              <Button
                onClick={() => onRemove(index)}
                className="buttonStyle__29"
                shape="circle"
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </Tooltip>
          </div>
        </div>
      ))}
      <DataValueEditor value={draft} onChange={setDraft} label="Argument" />
      <div className="row mt-2">
        <div className="col-12 col-md-3">
          <Button onClick={onAdd} className="generalStyles__width100 buttonStyle__28">
            <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
            Add Argument
          </Button>
        </div>
      </div>
    </div>
  );
}

/** LN2 command -> OPC UA method-call mappings: object node, method node, and ordered input arguments. */
export default function MethodsEditor({
  methods,
  onChangeMethods,
}: {
  methods: OpcUaMethodMappingForm[];
  onChangeMethods: (rows: OpcUaMethodMappingForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaMethodMappingForm>({
    msgId: "",
    active: true,
    objectId: { ...BLANK_NUMERIC_NODE_ID },
    methodId: { ...BLANK_NUMERIC_NODE_ID },
    inputArguments: [],
  });

  const canAdd =
    draft.msgId !== "" && isNodeComplete(draft.objectId) && isNodeComplete(draft.methodId);
  const onAdd = () => {
    if (!canAdd) return;
    onChangeMethods([...methods, draft]);
    setDraft({
      msgId: "",
      active: true,
      objectId: { ...BLANK_NUMERIC_NODE_ID },
      methodId: { ...BLANK_NUMERIC_NODE_ID },
      inputArguments: [],
    });
  };
  const onRemove = (index: number) => onChangeMethods(methods.filter((_, i) => i !== index));

  return (
    <div className="container mt-3">
      <Collapse defaultActiveKey={["methods"]}>
        <Panel header={t("general.opcUaMethodMappings", { total: methods.length })} key="methods">
          {methods.map((method, index) => (
            <div
              key={`${method.msgId}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {method.msgId} ({method.active ? "active" : "inactive"}) → object=
                  {describeNode(method.objectId)}; method={describeNode(method.methodId)} (
                  {method.inputArguments.length} arg(s))
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemove(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">LN2 Message</span>
              <SelectLn2Message
                value={draft.msgId}
                onChange={(key) => setDraft({ ...draft, msgId: key })}
              />
            </div>
            <div className="col-12 col-md-3 mt-2">
              <div className="generalStyles__flex">
                <span className="generalStyles__inputLabel">Active</span>
                <Switch
                  checked={draft.active}
                  onChange={(checked) => setDraft({ ...draft, active: checked })}
                  style={{ marginLeft: 8 }}
                />
              </div>
            </div>
          </div>
          <div className="mt-2">
            <span className="generalStyles__inputLabel">Object Node</span>
            <NodeIdEditor
              node={draft.objectId}
              onChangeNode={(objectId) => setDraft({ ...draft, objectId })}
            />
          </div>
          <div className="mt-3">
            <span className="generalStyles__inputLabel">Method Node</span>
            <NodeIdEditor
              node={draft.methodId}
              onChangeNode={(methodId) => setDraft({ ...draft, methodId })}
            />
          </div>
          <InputArgumentsEditor
            args={draft.inputArguments}
            onChangeArgs={(inputArguments) => setDraft({ ...draft, inputArguments })}
          />
          <div className="row mt-2">
            <div className="col-12 col-md-3">
              <Button
                onClick={onAdd}
                disabled={!canAdd}
                className={`generalStyles__width100 ${canAdd ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add Method Mapping
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
